// The actions 3.2 and 3.3 share: Run all tests (every test event in the batch's sandbox through the agent, then the
// status rule), Build, Make live, Roll back, New version. Each returns the console lines it printed, so the playground
// shows them and 3.2 toasts them.
import { useState } from 'react'
import { notify } from '@/kit/ui'
import { TEST_APPLICANT } from '@/fixtures/code'
import { AgentOffline, getAgent, runTestEvent } from '@/lib/agent'
import { batchName, codeKeyOf } from '@/lib/batch'
import { endpointLabel, endpointOf } from '@/lib/endpoints'
import { c1Check, followersOfBatch } from '@/lib/flow'
import { DEMO_PREV, openSandbox } from '@/lib/sandbox'
import { ApiError, apiMode } from '@/lib/hubApi'
import { useFlowStore } from '@/store/flowStore'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useCodeStore } from '@/store/codeStore'
import { useSettingsStore } from '@/store/settingsStore'
import type { Batch, Lender, TestEvent, TestResult } from '@/types'

const stamp = () => new Date().toLocaleTimeString('en-IN', { hour12: false })

export interface RunAllOptions {
  /** the test events to run (default: the batch's) */
  tests?: TestEvent[]
  /** record the result on the saved batch (false while the working copy is unsaved) */
  record?: boolean
  /** a sandbox that is already open (the playground); else one is opened from the store's copy */
  sandbox?: string
  /** the file of an endpoint in the working copy (the playground's manifest may be newer than the store's) */
  fileOf?: (route: string) => string | null
  mode?: 'sample' | 'real'
  environment?: 'uat' | 'production'
}

export function useBatchActions() {
  const { user, can } = useCurrentUser()
  const store = useCodeStore()
  const settings = useSettingsStore((s) => s.settings)
  const [busy, setBusy] = useState<string | null>(null)

  /** Runs every test event of the batch in its sandbox and records the result on the saved batch. */
  const runAll = async (batch: Batch, lender: Lender, opts: RunAllOptions = {}): Promise<{ results: TestResult[]; lines: string[] }> => {
    const tests = opts.tests ?? batch.tests
    const record = opts.record ?? true
    setBusy(batch.id)
    const lines: string[] = [`[${stamp()}] run all tests · ${batchName(batch)} · ${tests.length} test event${tests.length === 1 ? '' : 's'} · in the sandbox`]
    try {
      let sb = opts.sandbox
      if (!sb) {
        try {
          sb = (await openSandbox(lender, batch, { user, settings, environment: opts.environment })).id
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err)
          notify.error(err instanceof AgentOffline ? 'Playground agent offline' : 'Sandbox did not open', msg)
          return { results: [], lines: [...lines, `  ✗ ${msg}`] }
        }
      }
      const agent = getAgent()
      const results: TestResult[] = []
      for (const t of tests) {
        const file = opts.fileOf ? opts.fileOf(t.endpoint) : endpointOf(batch.manifest, t.endpoint)?.file ?? null
        if (!file) {
          results.push({ testId: t.id, endpoint: t.endpoint, name: t.name, expectedOutcome: t.expectedOutcome, actualOutcome: null, ok: false, log: [], error: `endpoint ${t.endpoint} is not in the code (Discover endpoints)`, value: undefined, ms: 0, issues: [] })
          continue
        }
        // one test that throws (network, the agent gone) is red; the others still run
        results.push(await runTestEvent(agent, sb, file, t, TEST_APPLICANT, { prev: DEMO_PREV, mode: opts.mode ?? 'sample', environment: opts.environment ?? 'uat' })
          .catch((err: unknown): TestResult => ({ testId: t.id, endpoint: t.endpoint, name: t.name, expectedOutcome: t.expectedOutcome, actualOutcome: null, ok: false, log: [], error: err instanceof Error ? err.message : String(err), value: undefined, ms: 0, issues: [] })))
      }
      const passed = results.filter((r) => r.ok).length
      for (const r of results) {
        lines.push(`  ${r.ok ? '✓' : '✗'} ${endpointLabel(r.endpoint)} · ${r.name}: expected ${r.expectedOutcome}, got ${r.actualOutcome ?? 'nothing'}${r.error ? ` · ${r.error}` : ''} · ${r.ms} ms`)
        for (const i of (r.issues ?? []).filter((x) => x.level === 'warning')) lines.push(`      ⚠ ${i.text}`)
        for (const l of r.log) lines.push(`      ${l}`)
      }
      lines.push(`  ${passed} / ${results.length} passed${passed === results.length ? ' → tests passed' : ' → still draft'}`)
      if (record) {
        const next = store.recordTestRun(batch.id, results, user.email)
        if (next.status === 'tests_passed') notify.success(`All ${results.length} tests pass`, can('code.publish') ? 'Build is enabled.' : 'An admin can build it now.')
        else notify.warning(`${results.length - passed} test${results.length - passed === 1 ? '' : 's'} fail`, 'Not approved until every test is green: see the console and Problems.')
      }
      return { results, lines }
    } finally {
      setBusy(null)
    }
  }

  const build = async (batch: Batch): Promise<string[]> => {
    setBusy(batch.id)
    const image = `hub-batch-${codeKeyOf(batch).replace(/\./g, '-')}:${batch.version}`
    const lines = [`[${stamp()}] build ${batchName(batch)} at commit ${batch.git.commit}${apiMode() ? ' · on the Hub (image, tests inside it, bundle, tag): this takes a little while' : ''}`]
    try {
      const built = await store.build(batch.id, user.email)
      if (apiMode()) {
        lines.push(`  1 · image ${image} from hub-sandbox: npm install, npm start answers /_hub/health with ${batch.manifest.endpoints.length} endpoint(s) ✓`, `  2 · every test event again inside the image: ${batch.tests.length} / ${batch.tests.length} ✓`, `  3 · pushed to our registry · bundle ${built.bundle!.key} in hub-media · ${built.bundle!.checksum.slice(0, 23)}…`, `  4 · git tag ${built.git.tag} on ${built.git.commit}`, '  5 · the version is fixed: read-only from here')
      } else {
        lines.push(`  1 · hub-discover → .hub/manifest.json: ${batch.manifest.endpoints.length} endpoint(s)`, `  2 · git tag ${built.git.tag} at ${built.git.commit}`, `  3 · bundle ${built.bundle!.key} · ${built.bundle!.checksum.slice(0, 23)}… (demo data: the image is made by the Hub API, npm run dev:api)`, '  4 · the version is fixed: read-only from here')
      }
      notify.success(`Built ${batch.version}`, `${built.bundle!.checksum.slice(0, 19)}… · Make live is enabled.`)
      return lines
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      const log = err instanceof ApiError && Array.isArray(err.extra.log) ? (err.extra.log as string[]).slice(-12).map((l) => `      ${l}`) : []
      notify.error('Build refused', msg)
      return [...lines, `  ✗ ${msg}`, ...log]
    } finally {
      setBusy(null)
    }
  }

  /** C1: the live workflows that follow the live batch, checked against the batch about to be live. */
  const c1 = (batch: Batch) => {
    const followers = followersOfBatch(useFlowStore.getState().flows, useCodeStore.getState().batches, batch)
    const problems = followers.flatMap((f) => c1Check(f, batch))
    return { moved: followers.map((f) => `${f.name.split('.').slice(1).join('.')} ${f.version}`), problems }
  }

  /** On the API: the Hub's own C1, the runner's container, the switch; a C1 refusal lists its problems. */
  const onHub = async (batch: Batch, kind: 'live' | 'back'): Promise<string[]> => {
    const lines = [`[${stamp()}] ${kind === 'live' ? 'make live' : 'roll back to'} ${batchName(batch)} (on the Hub)`]
    setBusy(batch.id)
    try {
      const r = await store.switchOnHub(batch.id, kind)
      lines.push(`  1 · C1 on the Hub: every endpoint and outcome the live workflows use exists in ${batch.version} ✓`)
      lines.push(`  2 · the runner's container hub-run-${codeKeyOf(batch).replace(/\./g, '-')}-${batch.version.replace(/\./g, '-')}-prod ${kind === 'live' ? 'started from the image in our registry (by digest) with the production variables from the vault' : 'kept from before'}; /_hub/health ✓`)
      lines.push(r.moved.length ? `  3 · routed to ${batch.version}: ${r.moved.join(', ')}` : `  3 · routed to ${batch.version}; no live workflow follows the live batch`)
      if (r.previous) lines.push(`  4 · ${r.previous} is previous (its container kept for roll-back)${r.retired ? `, ${r.retired} retired and stopped` : ''}`)
      if (kind === 'live') notify.success(`${batch.version} is live`, r.moved.length ? `${r.moved.join(', ')} now run it.` : 'Live and read-only.')
      else notify.warning(`Rolled back to ${batch.version}`, 'Live again; the replaced version is previous.')
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      const problems = err instanceof ApiError && Array.isArray(err.extra.problems) ? (err.extra.problems as string[]) : []
      notify.error(`${kind === 'live' ? 'Make live' : 'Roll back'} refused${problems.length ? ' (C1)' : ''}`, msg)
      lines.push(`  ✗ ${msg}`, ...problems.map((p) => `      ${p}`))
    } finally {
      setBusy(null)
    }
    return lines
  }

  const makeLive = async (batch: Batch): Promise<string[]> => {
    if (apiMode()) return onHub(batch, 'live')
    const lines = [`[${stamp()}] make live ${batchName(batch)}`]
    try {
      const { moved, problems } = c1(batch)
      if (problems.length) {
        lines.push(`  ✗ C1 check: ${problems.length} problem${problems.length === 1 ? '' : 's'}; nothing changed`, ...problems.map((p) => `      ${p}`))
        notify.error('Make live refused (C1)', `${problems[0]}${problems.length > 1 ? ` (+${problems.length - 1} more)` : ''}`)
        return lines
      }
      const change = store.makeLive(batch.id, user.email, moved)
      lines.push(`  1 · the runner pulls image lender-${batch.lenderSlug}:${batch.version} by digest and starts lender-${batch.lenderSlug}-${batch.version.replace(/\./g, '')} with the prod variables from the vault ✓`)
      lines.push(`  2 · health check GET /_hub/health ✓ · C1: every endpoint and outcome the live workflows use exists in ${batch.version} ✓`)
      lines.push(moved.length ? `  3 · routed to ${batch.version}: ${moved.join(', ')}` : '  3 · no live workflow follows the live batch')
      if (change.previous) lines.push(`  4 · ${change.previous} is previous (its container kept for roll-back)${change.retired ? `, ${change.retired} retired and stopped` : ''}`)
      notify.success(`${batch.version} is live`, moved.length ? `${moved.join(', ')} now run it.` : 'Live and read-only.')
    } catch (err) {
      notify.error('Make live refused', err instanceof Error ? err.message : String(err))
      lines.push(`  ✗ ${err instanceof Error ? err.message : String(err)}`)
    }
    return lines
  }

  const rollBack = async (batch: Batch): Promise<string[]> => {
    if (apiMode()) return onHub(batch, 'back')
    const lines = [`[${stamp()}] roll back to ${batchName(batch)}`]
    try {
      const { moved, problems } = c1(batch)
      if (problems.length) {
        lines.push(`  ✗ C1 check: ${problems.length} problem${problems.length === 1 ? '' : 's'}; nothing changed`, ...problems.map((p) => `      ${p}`))
        notify.error('Roll back refused (C1)', problems[0]!)
        return lines
      }
      const change = store.rollBack(batch.id, user.email, moved)
      lines.push(`  1 · the kept container of ${batch.version} takes the route again (no rebuild) ✓`)
      lines.push(moved.length ? `  2 · C1 ✓ · routed back to ${batch.version}: ${moved.join(', ')}` : '  2 · no live workflow to move')
      if (change.previous) lines.push(`  3 · ${change.previous} is previous now`)
      notify.warning(`Rolled back to ${batch.version}`, 'Live again; the replaced version is previous.')
    } catch (err) {
      notify.error('Roll back refused', err instanceof Error ? err.message : String(err))
      lines.push(`  ✗ ${err instanceof Error ? err.message : String(err)}`)
    }
    return lines
  }

  /** BP-1: `productSlug` = which product's batch (left out: the product of `fromId`) */
  const newVersion = (slug: string, fromId: string | null, version: string | null, productSlug?: string | null): Batch | null => {
    try {
      const b = store.newVersion(slug, fromId, version, user.email, productSlug)
      notify.success(`Version ${b.version} created`, `Copied from ${b.madeFrom ?? 'nothing'} on branch ${b.git.branch}. Edit, test, build, make live.`)
      return b
    } catch (err) {
      notify.error('New version refused', err instanceof Error ? err.message : String(err))
      return null
    }
  }

  return { user, can, busy, runAll, build, makeLive, rollBack, newVersion }
}
